import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { FIRST_INDEX, FORM_LEN, MIN_LEN } from '@/constants';
import { useAppDispatch, useAppSelector } from '@/store';
import { verifyOtpThunk } from '@/store/thunks/auth/verifyOtpThunk';
import { resendRegistrationOtpThunk } from '@/store/thunks/auth/resendRegistrationOtpThunk';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { selectAuthError } from '@/store/selectors/auth';
import { selectRegistrationData } from '@/store/selectors/registration';
import { setData } from '@/store/slices/registration/registrationSlice';
import { setRegistrationStep } from '@/utils/sessionStorage';

const SETUP_CODE_LENGTH = 12;

const schema = z.object({
  username: z.string().email('Please enter a valid email').nonempty('Email is required'),
  otp: z.string().min(FORM_LEN, 'OTP is required'),
});
const emailSchema = z.string().email('Please enter a valid email').nonempty('Email is required');

type FormValues = z.infer<typeof schema>;

export interface UseUserFormState {
  email: string;
  setupCode: string;
  hasSubmitted: boolean;
  emailError: string | undefined;
  setupCodeError: string | undefined;
  isSubmitting: boolean;
  isResending: boolean;
  isContinueDisabled: boolean;
  isResendDisabled: boolean;
  globalError: string | undefined;
}

export interface UseUserFormHandlers {
  handleEmailChange: (value: string) => void;
  handleSetupCodeChange: (value: string) => void;
  handleSubmit: () => void;
  handleResendOtp: () => void;
}

export interface UseUserFormReturn {
  state: UseUserFormState;
  handlers: UseUserFormHandlers;
}

export function useUserForm(): UseUserFormReturn {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { handlers: loaderHandlers } = useGlobalLoader();
  const authError = useAppSelector(selectAuthError);
  const formData = useAppSelector(selectRegistrationData);

  const [email, setEmail] = useState(formData.username ?? '');
  const [setupCode, setSetupCode] = useState(formData.otp ?? '');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isLocallySubmitting, setIsLocallySubmitting] = useState(false);
  const [isLocallyResending, setIsLocallyResending] = useState(false);

  const emailError = useMemo(() => {
    if (!hasSubmitted) return undefined;

    const trimmed = email.trim();
    if (trimmed.length === MIN_LEN) return 'Email is required';

    const parsed = emailSchema.safeParse(trimmed);
    if (!parsed.success) {
      return parsed.error.issues[FIRST_INDEX]?.message ?? 'Please enter a valid email';
    }
    return undefined;
  }, [email, hasSubmitted]);

  const setupCodeError = useMemo(() => {
    if (!hasSubmitted) return undefined;

    const trimmed = setupCode.trim();
    if (trimmed.length === MIN_LEN) return 'OTP is required';
    if (trimmed.length !== SETUP_CODE_LENGTH) return `OTP must be ${SETUP_CODE_LENGTH} characters`;

    return undefined;
  }, [setupCode, hasSubmitted]);

  const isSubmitting = isLocallySubmitting;
  const isResending = isLocallyResending;
  const formIncomplete = email === '' || setupCode === '';
  const submittedWithErrors = hasSubmitted && (emailError !== undefined || setupCodeError !== undefined);
  const hasValidEmailForResend = emailSchema.safeParse(email.trim()).success;
  const isResendDisabled = isLocallyResending || !hasValidEmailForResend;
  const isContinueDisabled =
    isSubmitting || submittedWithErrors || formIncomplete;

  const handleEmailChange = useCallback((value: string): void => {
    setEmail(value);
  }, []);

  const handleSetupCodeChange = useCallback((value: string): void => {
    setSetupCode(value);
  }, []);

  const handleSubmit = useCallback(() => {
    setHasSubmitted(true);

    const values: FormValues = { username: email.trim(), otp: setupCode.trim() };
    const parsed = schema.safeParse(values);
    if (!parsed.success) return;
    if (parsed.data.otp.length !== SETUP_CODE_LENGTH) return;

    setIsLocallySubmitting(true);
    loaderHandlers.showLoader();
    void (async() => {
      try {
        const action = await dispatch(verifyOtpThunk(parsed.data));
        if (verifyOtpThunk.fulfilled.match(action)) {
          dispatch(setData({ username: parsed.data.username, otp: parsed.data.otp }));
          setRegistrationStep('emailCode');
          await navigate({ to: '/registration/verify-email' });
        }
      } finally {
        loaderHandlers.hideLoader();
        setIsLocallySubmitting(false);
      }
    })();
  }, [dispatch, email, loaderHandlers, navigate, setupCode]);

  const handleResendOtp = useCallback(() => {
    const username = email.trim();
    const parsed = emailSchema.safeParse(username);
    if (!parsed.success) {
      setHasSubmitted(true);
      return;
    }

    void (async() => {
      setIsLocallyResending(true);
      try {
        await dispatch(resendRegistrationOtpThunk({ username }));
      } finally {
        setIsLocallyResending(false);
      }
    })();
  }, [dispatch, email]);

  return {
    state: {
      email,
      setupCode,
      hasSubmitted,
      emailError,
      setupCodeError,
      isSubmitting,
      isResending,
      isContinueDisabled,
      isResendDisabled,
      globalError: authError,
    },
    handlers: {
      handleEmailChange,
      handleSetupCodeChange,
      handleSubmit,
      handleResendOtp,
    },
  };
}
